// «Рынок → Розница»: средние потребительские цены Росстата по территории.
// Территорий 380, поэтому ряд спрашивается по одной; Россия — пустая строка.
// Позиция Росстата вида не называет — строка подписана группой.

import { useQuery } from '@tanstack/react-query'
import { cabinetApi } from '../api'
import { DASH, formatMonth, formatNumber, useLang, useT } from '../i18n'
import { useOrg } from '../org'
import { ChainsPanel } from './ChainsPanel'
import { productKeys } from './customs'
import { usePricesUrl } from './pricesUrl'
import { retailMonths, retailRows, retailYears } from './retail'

const MONTHS_SHOWN = 6

type RetailSource = 'rosstat' | 'tonusmeal'

/** Розница: два источника. Tonusmeal (цены сетей) — внутренний, только кабинету с
 *  `prices_internal`; остальным вкладка источника не показывается вовсе. */
export function RetailSection() {
  const t = useT()
  const internal = useOrg().activeOrg?.services?.prices_internal === true
  const url = usePricesUrl()
  // `tonus` — прежнее значение адреса (до 27.10.2026): разосланные ссылки открывают ту же вкладку.
  const src: RetailSource = ['tonusmeal', 'tonus'].includes(url.get('retailSource') ?? '')
    ? 'tonusmeal' : 'rosstat'
  const setSrc = (k: RetailSource) => url.set({ retailSource: k === 'rosstat' ? null : k })
  const shown = internal ? src : 'rosstat'
  return (
    <div className="space-y-4">
      {internal && (
        <div className="flex flex-wrap gap-2 text-xs">
          {(['rosstat', 'tonusmeal'] as const).map((k) => (
            <button key={k} type="button" onClick={() => setSrc(k)}
              className={`rounded border px-3 py-1 ${shown === k
                ? 'border-accent text-foam' : 'border-line text-mist hover:text-foam'}`}>
              {t(`prices.retail.source.${k}`)}
            </button>
          ))}
        </div>
      )}
      {shown === 'rosstat' ? <RetailPanel /> : <ChainsPanel />}
    </div>
  )
}

export function RetailPanel() {
  const t = useT()
  const { lang } = useLang()
  const terr = useQuery({ queryKey: ['cabinet', 'prices', 'retail'],
    queryFn: () => cabinetApi.retailTerritories(), retry: false })
  const url = usePricesUrl()
  const raw = url.get('retailTerritory') ?? ''
  const city = terr.data && !terr.data.territories.includes(raw) ? '' : raw
  const setCity = (c: string) => url.set({ retailTerritory: c })
  const series = useQuery({ queryKey: ['cabinet', 'prices', 'retail', city],
    queryFn: () => cabinetApi.retailSeries(city), retry: false,
    enabled: !!terr.data?.territories.length })

  if (terr.isLoading) return <p className="text-sm text-mist">{t('prices.loading')}</p>
  if (terr.error) return <p className="text-sm text-mist">{t('prices.error')}</p>
  const list = terr.data?.territories ?? []
  if (!list.length) return <p className="text-sm text-foam">{t('prices.retail.empty')}</p>

  const data = series.data
  const rows = data ? retailRows(data) : []
  const months = data ? retailMonths(data, MONTHS_SHOWN) : []
  const years = data ? retailYears(data) : []
  const label = (c: string) => (c === '' ? t('prices.retail.russia') : c)
  const outlook = new Map((data?.outlook ?? []).map((o) => [o.item_key, o]))
  const outMonth = data?.outlook[0]?.month

  return (
    <div className="space-y-4 text-sm">
      <label className="flex items-center gap-2 text-mist">
        {t('prices.retail.territory')}
        <select value={city} onChange={(e) => setCity(e.target.value)}
          className="max-w-md rounded border border-line bg-transparent px-2 py-1 text-foam">
          {list.map((c) => <option key={c} value={c}>{label(c)}</option>)}
        </select>
      </label>
      {series.isLoading && <p className="text-mist">{t('prices.loading')}</p>}
      {data && (
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse text-xs">
            <thead>
              <tr className="text-left text-mist">
                <th className="py-1 pr-3 font-normal">{t('prices.export.product')}</th>
                {years.map((y) => <th key={y} className="py-1 pr-3 text-right font-normal">{y}</th>)}
                {months.map((m) => (
                  <th key={m} className="py-1 pr-3 text-right font-normal">{formatMonth(lang, m)}</th>
                ))}
                {outMonth && (
                  <th className="py-1 pr-3 text-right font-normal text-accent"
                    title={t('prices.retail.outlookTip')}>
                    {t('prices.retail.outlook', { month: formatMonth(lang, outMonth) })}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.item_key} className="border-t border-line/50 text-foam">
                  <td className="py-1 pr-3">
                    {lang === 'ru' ? r.name_ru : r.name_en}
                    <span className="text-mist">
                      {' · '}{productKeys(r).map((k) => t(k)).join(', ')}
                      {r.pack && ` · ${r.pack}`}
                    </span>
                  </td>
                  {years.map((y) => {
                    const c = r.years[y]
                    return (
                      <td key={y} className="py-1 pr-3 text-right tabular-nums"
                        title={c ? t('prices.retail.yearTip', { months: c.months }) : undefined}>
                        {c ? formatNumber(lang, c.rub, 2) : DASH}
                      </td>
                    )
                  })}
                  {months.map((m) => (
                    <td key={m} className="py-1 pr-3 text-right tabular-nums">
                      {r.months[m] !== undefined ? formatNumber(lang, r.months[m], 2) : DASH}
                    </td>
                  ))}
                  {outMonth && (
                    <td className="py-1 pr-3 text-right tabular-nums text-mist">
                      {outlook.has(r.item_key) ? formatNumber(lang, outlook.get(r.item_key)!.rub_per_kg, 2) : DASH}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-2 text-xs text-mist">{t('prices.retail.unit')}</p>
          <p className="mt-1 text-xs text-mist">{t('prices.retail.note')}</p>
        </div>
      )}
    </div>
  )
}
